var DomainException = require('../DomainException');
const AggregateRoot = require('../ddd/AggregateRoot');
const _ = require('../../lodash');
const { newGuid } = require('../../utils');
const getConstants = require('../../utils/getConstants');
const constants = getConstants();
const { issueStates, issueDeadlineSourceTypes, whoAssignmentTypes, whoUnionType } = constants;
const moment = require('moment-timezone');

function isNullOrEmptyOrWhitespace(str) {
   return str === undefined || str === null || str.trim() === '';
}

function calculatePriorityDueBy({ raisedOn, resolutionTargetMins }) {
   if (resolutionTargetMins == null) return null;
   return moment.utc(raisedOn).add(resolutionTargetMins, 'minutes').toDate();
}

class Issue extends AggregateRoot {
   constructor({ user, logger }) {
      super({ user, logger });
   }

   get agt() {
      return 'Issue';
   }

   async createNew({
      tid,
      idIssue,
      noIssue,
      title,
      description = null,
      dueBy,
      noPriority,
      resolutionTargetMins = null,
      zonedFor,
      noLocation,
      nosLocation,
      raisedOn,
      noRaisedBy,
      idChannel
   }) {
      idChannel = idChannel || newGuid();

      const hasManualDeadline = dueBy != null;
      const calculatedDueBy = hasManualDeadline ? dueBy : calculatePriorityDueBy({ raisedOn, resolutionTargetMins });
      const noDueBySource = hasManualDeadline
         ? issueDeadlineSourceTypes.MANUAL.id
         : noPriority != null
         ? issueDeadlineSourceTypes.PRIORITY.id
         : null;

      let p = {
         aid: idIssue,
         noIssue,
         tid,
         idChannel,
         title,
         description,
         dueBy: calculatedDueBy,
         noDueBySource,
         state: issueStates.NEW,
         noPriority,
         closedReason: null,
         zonedFor,
         noLocation,
         nosLocation,
         raisedOn,
         noRaisedBy,
         noWhoAssignmentType: whoAssignmentTypes.UNASSIGNED.id,
         noWhoUnionType: whoUnionType.ANY.id,
         teams: [],
         positions: [],
         staff: []
      };

      this.apply('ISSUE_RAISED', p);

      const o = _.omit(p, [
         'aid',
         'tid',
         'idChannel',
         'noLocation',
         'nosLocation',
         'dueBy',
         'noDueBySource',
         'zonedFor',
         'noWhoAssignmentType',
         'noWhoUnionType',
         'teams',
         'positions',
         'staff'
      ]);

      this.setData(
         {
            properties: _.merge(
               {
                  id: p.aid,
                  deleted: false,
                  isTemplate: false
               },
               o,
               {
                  actionedBy: { noUser: null }
               },
               {
                  closedBy: { noUser: null }
               },
               {
                  tags: [],
                  channel: {
                     id: p.idChannel,
                     count: 0
                  },
                  timing: {
                     dueBy: p.dueBy,
                     noDueBySource: p.noDueBySource,
                     actionedOn: null,
                     closedOn: null,
                     zonedFor
                  },
                  where: {
                     noLocation: p.noLocation,
                     nosLocation: p.nosLocation
                  },
                  who: {
                     noWhoAssignmentType: whoAssignmentTypes.UNASSIGNED.id,
                     noWhoUnionType: whoUnionType.ANY.id,
                     assignees: null,
                     teams: [],
                     positions: [],
                     staff: []
                  },
                  minsTo: {
                     assign: null,
                     action: null,
                     resolve: null,
                     close: null
                  }
               }
            )
         },
         true
      );
   }

   getChannelId() {
      return this._properties.channel ? this._properties.channel.id : null;
   }

   ensureChannel({ idChannel }) {
      if (this.getChannelId()) {
         return this.getChannelId();
      }

      idChannel = idChannel || newGuid();

      const p = {
         noIssue: this._properties.noIssue,
         idIssue: this.id,
         idChannel
      };

      this.apply('ISSUE_CHANNEL_ENSURED', p);

      this._properties.channel = {
         id: idChannel,
         count: 0
      };

      return idChannel;
   }

   ensureCanAddComment() {
      const isClosed = (this._properties.state & issueStates.CLOSED) === issueStates.CLOSED;

      if (isClosed) {
         throw new DomainException('ADD_ISSUE_COMMENT - Closed issues cannot receive comments');
      }
   }

   addComment({ idFrame, comment, meta = {}, actionedOn }) {
      this.ensureCanAddComment();

      const p = {
         noIssue: this._properties.noIssue,
         idIssue: this.id,
         actionedOn,
         idFrame,
         comment,
         meta,
         noOfComments: ((this._properties.channel && this._properties.channel.count) || 0) + 1
      };

      this.apply('ISSUE_COMMENT_ADDED', p);

      this._properties.channel = this._properties.channel || {};
      this._properties.channel.count = p.noOfComments;
   }

   deleteComment({ idFrame, actionedOn }) {
      const currentCount = (this._properties.channel && this._properties.channel.count) || 0;
      const p = {
         noIssue: this._properties.noIssue,
         idIssue: this.id,
         actionedOn,
         idFrame,
         noOfComments: Math.max(currentCount - 1, 0)
      };

      this.apply('ISSUE_COMMENT_DELETED', p);

      this._properties.channel = this._properties.channel || {};
      this._properties.channel.count = p.noOfComments;
   }

   unassignFromEveryone() {
      const { noIssue, state } = this._properties;

      const newState = state & ~issueStates.ASSIGNED;

      const p = {
         noWhoUnionType: whoUnionType.ANY.id,
         noWhoAssignmentType: whoAssignmentTypes.UNASSIGNED.id,
         noIssue,
         idIssue: this.id,
         assignees: null,
         state: newState
      };

      this.apply('ISSUE_UNASSIGNED_FROM_EVERYONE', p);

      const who = this._properties.who;

      this._properties = {
         ...this._properties,
         state: newState,
         who: {
            ...who,
            noWhoAssignmentType: p.noWhoAssignmentType,
            noWhoUnionType: p.noWhoUnionType,
            assignees: null,
            teams: [],
            positions: [],
            staff: []
         }
      };
   }

   assignSpecificTeams({ nosTeam, noWhoAssignmentType, noUserAssignedBy, assignedOn, assignees = null }) {
      const { noIssue, state } = this._properties;

      const newState = state | issueStates.ASSIGNED;

      let { noWhoAssignmentType: noExistingWhoAssignmentType } = this._properties.who;

      let willAssignPositions =
         (noWhoAssignmentType & whoAssignmentTypes.SPECIFIC_POSITIONS.id) == whoAssignmentTypes.SPECIFIC_POSITIONS.id;
      let willAssignStaff = (noWhoAssignmentType & whoAssignmentTypes.SPECIFIC_STAFF.id) == whoAssignmentTypes.SPECIFIC_STAFF.id;

      let newWhoAssignmentType = noExistingWhoAssignmentType;
      newWhoAssignmentType = !willAssignPositions ? newWhoAssignmentType & ~whoAssignmentTypes.SPECIFIC_POSITIONS.id : newWhoAssignmentType;
      newWhoAssignmentType = !willAssignStaff ? newWhoAssignmentType & ~whoAssignmentTypes.SPECIFIC_STAFF.id : newWhoAssignmentType;

      const minsToAssign = this.buildSLA('assign');

      const p = {
         noUserAssignedBy,
         assignedOn,
         assignees,
         nosTeam,
         minsToAssign,
         noWhoAssignmentType: newWhoAssignmentType | whoAssignmentTypes.SPECIFIC_TEAMS.id,
         willAssignPositions,
         willAssignStaff,
         state: newState,
         noIssue,
         idIssue: this.id,
         noLocation: this._properties.where ? this._properties.where.noLocation : null
      };

      this.apply('ISSUE_ASSIGNEDTO_SPECIFIC_TEAMS', p);

      const who = this._properties.who;
      this._properties = {
         ...this._properties,
         state: newState,
         minsTo: {
            ...this._properties.minsTo,
            assign: minsToAssign
         },
         who: {
            ...who,
            noWhoAssignmentType: p.noWhoAssignmentType,
            assignees: p.assignees,
            teams: _.map(nosTeam, (l) => {
               return { no: l };
            }),
            positions: willAssignPositions ? who.positions : [],
            staff: willAssignStaff ? who.staff : []
         }
      };
   }

   assignSpecificPositions({ nosPosition, noWhoAssignmentType, noUserAssignedBy, assignedOn, assignees = null }) {
      const { noIssue, state, who } = this._properties;
      const { noWhoAssignmentType: noExistingWhoAssignmentType } = who;

      const newState = state | issueStates.ASSIGNED;

      const willAssignTeams = (noWhoAssignmentType & whoAssignmentTypes.SPECIFIC_TEAMS.id) == whoAssignmentTypes.SPECIFIC_TEAMS.id;
      const willAssignStaff = (noWhoAssignmentType & whoAssignmentTypes.SPECIFIC_STAFF.id) == whoAssignmentTypes.SPECIFIC_STAFF.id;

      let newWhoAssignmentType = noExistingWhoAssignmentType;
      newWhoAssignmentType = !willAssignTeams ? newWhoAssignmentType & ~whoAssignmentTypes.SPECIFIC_TEAMS.id : newWhoAssignmentType;
      newWhoAssignmentType = !willAssignStaff ? newWhoAssignmentType & ~whoAssignmentTypes.SPECIFIC_STAFF.id : newWhoAssignmentType;

      const minsToAssign = this.buildSLA('assign');

      const p = {
         noUserAssignedBy,
         assignedOn,
         assignees,
         nosPosition,
         minsToAssign,
         noWhoAssignmentType: newWhoAssignmentType | whoAssignmentTypes.SPECIFIC_POSITIONS.id,
         willAssignTeams,
         willAssignStaff,
         state: newState,
         noIssue,
         idIssue: this.id,
         noLocation: this._properties.where ? this._properties.where.noLocation : null
      };

      this.apply('ISSUE_ASSIGNEDTO_SPECIFIC_POSITIONS', p);

      this._properties = {
         ...this._properties,
         state: newState,
         minsTo: {
            ...this._properties.minsTo,
            assign: minsToAssign
         },
         who: {
            ...who,
            noWhoAssignmentType: p.noWhoAssignmentType,
            assignees: p.assignees,
            positions: _.map(nosPosition, (l) => {
               return { no: l };
            })
         }
      };
   }

   assignSpecificStaff({ nosStaff, noWhoAssignmentType, noUserAssignedBy, assignedOn, assignees = null }) {
      const { noIssue, state, who } = this._properties;
      const { noWhoAssignmentType: noExistingWhoAssignmentType } = who;

      const newState = state | issueStates.ASSIGNED;

      let willAssignPositions =
         (noWhoAssignmentType & whoAssignmentTypes.SPECIFIC_POSITIONS.id) == whoAssignmentTypes.SPECIFIC_POSITIONS.id;
      let willAssignTeams = (noWhoAssignmentType & whoAssignmentTypes.SPECIFIC_TEAMS.id) == whoAssignmentTypes.SPECIFIC_TEAMS.id;

      let newWhoAssignmentType = noExistingWhoAssignmentType;
      newWhoAssignmentType = !willAssignTeams ? newWhoAssignmentType & ~whoAssignmentTypes.SPECIFIC_TEAMS.id : newWhoAssignmentType;
      newWhoAssignmentType = !willAssignPositions ? newWhoAssignmentType & ~whoAssignmentTypes.SPECIFIC_POSITIONS.id : newWhoAssignmentType;

      const minsToAssign = this.buildSLA('assign');

      const p = {
         noUserAssignedBy,
         assignedOn,
         assignees,
         nosStaff,
         minsToAssign,
         noWhoAssignmentType: newWhoAssignmentType | whoAssignmentTypes.SPECIFIC_STAFF.id,
         willAssignPositions,
         willAssignTeams,
         state: newState,
         noIssue,
         idIssue: this.id,
         noLocation: this._properties.where ? this._properties.where.noLocation : null
      };

      this.apply('ISSUE_ASSIGNEDTO_SPECIFIC_STAFF', p);

      this._properties = {
         ...this._properties,
         state: newState,
         minsTo: {
            ...this._properties.minsTo,
            assign: minsToAssign
         },
         who: {
            ...who,
            noWhoAssignmentType: p.noWhoAssignmentType,
            assignees: p.assignees,
            staff: _.map(nosStaff, (l) => {
               return { no: l };
            })
         }
      };
   }

   updateDeadline({ dueBy }) {
      this.setDeadline({ dueBy, noDueBySource: issueDeadlineSourceTypes.MANUAL.id });
   }

   resetDeadlineToPriority({ resolutionTargetMins = null }) {
      const dueBy = calculatePriorityDueBy({ raisedOn: this._properties.raisedOn, resolutionTargetMins });
      const noDueBySource = this._properties.noPriority == null ? null : issueDeadlineSourceTypes.PRIORITY.id;
      this.setDeadline({ dueBy, noDueBySource });
   }

   setDeadline({ dueBy, noDueBySource }) {
      const before = _.pick(this._properties.timing, ['dueBy', 'noDueBySource']);

      const p = { after: { dueBy, noDueBySource }, before, noIssue: this._properties.noIssue };

      this.apply('ISSUE_DEADLINE_UPDATED', p);

      this._properties = {
         ...this._properties,
         timing: {
            ...this._properties.timing,
            dueBy: p.after.dueBy,
            noDueBySource: p.after.noDueBySource
         }
      };
   }

   updateLocation({ noLocation, nosLocation }) {
      const before = _.pick(this._properties.where, ['noLocation', 'nosLocation']);

      var p = { after: { noLocation, nosLocation }, before, noIssue: this._properties.noIssue };

      this.apply('ISSUE_LOCATION_UPDATED', p);

      this._properties = {
         ...this._properties,
         where: {
            ...this._properties.where,
            noLocation: p.after.noLocation,
            nosLocation: p.after.nosLocation
         }
      };
   }

   updateDefine({ title, description = this._properties.description ?? null, noPriority, resolutionTargetMins = null }) {
      const before = _.pick(this._properties, ['title', 'description', 'noPriority']);
      const priorityChanged = before.noPriority !== noPriority;

      const p = { after: { title, description, noPriority }, before, noIssue: this._properties.noIssue, idIssue: this.id };

      this.apply('ISSUE_DEFINE_UPDATED', p);

      this._properties = {
         ...this._properties,
         title: p.after.title,
         description: p.after.description,
         noPriority: p.after.noPriority
      };

      if (priorityChanged && this._properties.timing.noDueBySource !== issueDeadlineSourceTypes.MANUAL.id) {
         this.resetDeadlineToPriority({ resolutionTargetMins });
      }
   }

   delete() {
      const p = { deleted: true, noIssue: this._properties.noIssue, idIssue: this.id };

      this.apply('ISSUE_DELETED', p);

      this._properties.deleted = true;
   }

   close({ noUserClosedBy, closedOn, isResolved, reason }) {
      const isAlreadyClosed = (this._properties.state & issueStates.CLOSED) === issueStates.CLOSED;

      if (isAlreadyClosed) {
         return;
      }

      let newState = this._properties.state | issueStates.CLOSED;
      let closedReason = null;
      let minsToClose = this.buildSLA('close');
      let minsToResolve = null;

      if (isResolved) {
         newState |= issueStates.RESOLVED;
         minsToResolve = this.buildSLA('resolve');
      } else {
         if (isNullOrEmptyOrWhitespace(reason)) {
            throw new DomainException('CLOSE_ISSUE - Reason is required when unresolved');
         }
         closedReason = reason;
      }

      const p = {
         state: newState,
         closedReason,
         noUserClosedBy,
         closedOn,
         minsToClose,
         minsToResolve,
         noIssue: this._properties.noIssue,
         idIssue: this.id
      };

      this.apply('ISSUE_CLOSED', p);

      this._properties = {
         ...this._properties,
         state: newState,
         closedReason: reason,
         closedBy: {
            ...this._properties.closedBy,
            noUser: noUserClosedBy
         },
         minsTo: {
            ...this._properties.minsTo,
            close: minsToClose,
            resolve: minsToResolve
         },
         timing: {
            ...this._properties.timing,
            closedOn: closedOn
         }
      };
   }

   reopen({ noUserReopenedBy, reopenedOn }) {
      const isClosed = (this._properties.state & issueStates.CLOSED) === issueStates.CLOSED;

      if (!isClosed) {
         return;
      }

      const newState = this._properties.state & ~issueStates.CLOSED & ~issueStates.RESOLVED;

      const p = {
         state: newState,
         closedOn: null,
         noUserClosedBy: null,
         closedReason: null,
         noUserReopenedBy,
         reopenedOn,
         noIssue: this._properties.noIssue,
         idIssue: this.id
      };

      this.apply('ISSUE_REOPENED', p);

      this._properties = {
         ...this._properties,
         state: newState,
         closedReason: null,
         closedBy: {
            ...this._properties.closedBy,
            noUser: null
         },
         timing: {
            ...this._properties.timing,
            closedOn: null
         }
      };
   }

   action({ noUserActionedBy, actionedOn }) {
      const newState = this._properties.state | issueStates.ACTIONED;
      const minsToAction = this.buildSLA('action');

      const p = {
         minsToAction,
         state: newState,
         noUserActionedBy,
         actionedOn,
         noIssue: this._properties.noIssue,
         idIssue: this.id
      };

      this.apply('ISSUE_ACTIONED', p);

      this._properties = {
         ...this._properties,
         state: newState,
         actionedBy: {
            noUser: noUserActionedBy
         },
         timing: {
            ...this._properties.timing,
            actionedOn
         },
         minsTo: {
            ...this._properties.minsTo,
            action: minsToAction
         }
      };
   }

   tag({ tag }) {
      const { noTag, name, noTagType, noParent: _noParent } = tag;

      this._properties.tags = this._properties.tags || [];
      let tags = this._properties.tags;

      var i = _.find(tags, (x) => {
         return x.noTag == noTag;
      });
      if (i) {
         return;
      } // already tagged

      const p = {
         name,
         noTagType,
         noTag,
         noIssue: this._properties.noIssue,
         idIssue: this.id
      };

      this.apply('ISSUE_TAGGED', p);

      tags.push({ noTag });
   }

   untag({ tag }) {
      if (!tag) {
         return;
      }

      const { noTag, name, noTagType, noParent: _noParent } = tag;

      this._properties.tags = this._properties.tags || [];
      let tags = this._properties.tags;

      var i = _.find(tags, (x) => {
         return x.noTag == noTag;
      });
      if (!i) {
         return;
      } // already untagged

      const p = {
         name,
         noTagType,
         noTag,
         noIssue: this._properties.noIssue,
         idIssue: this.id
      };

      this.apply('ISSUE_UNTAGGED', p);

      var slx = _.findIndex(tags, (x) => {
         return x.noTag === p.noTag;
      });
      if (slx > -1) {
         tags.splice(slx, 1);
      }
   }

   // protected

   buildSLA(slaName) {
      var raisedOn = this._properties.raisedBy ? this._properties.raisedBy.on : this._ctd;

      var duration = moment.duration(moment.utc().diff(moment.utc(raisedOn)));
      return this.isSLARecorded(slaName) ? this.getSLA(slaName) : Math.ceil(duration.asMinutes());
   }

   getSLA(slaName) {
      return this.isSLARecorded(slaName) ? this._properties.minsTo[slaName] : null;
   }

   isSLARecorded(slaName) {
      var notRecorded = !this._properties.minsTo || this._properties.minsTo[slaName] == null;
      return !notRecorded;
   }
}

module.exports = Issue;
